/**
 * Shared utility functions used across the application.
 * Consolidates duplicated logic (JWT parsing, ID generation, etc.)
 */

/**
 * Decode a JWT payload without verification (client-side only).
 * @param {string} token - JWT string
 * @returns {object|null} - Decoded payload or null on failure
 */
export function parseJWT(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

/**
 * Check if a JWT token is expired.
 * @param {string} token - JWT string
 * @returns {boolean}
 */
export function isJWTExpired(token) {
  const payload = parseJWT(token);
  if (!payload || !payload.exp) return true;
  return Date.now() / 1000 > payload.exp;
}

/**
 * Get the user ID (sub claim) from a JWT token.
 * @param {string} token - JWT string
 * @returns {string|null}
 */
export function getUserIdFromJWT(token) {
  const payload = parseJWT(token);
  return payload?.sub || null;
}

/**
 * Generate a random hex ID (32 chars / 16 bytes).
 * Uses crypto.getRandomValues for secure randomness.
 * @returns {string}
 */
export function generateId() {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
}
